import { reactive, computed, watch } from 'vue';
import type { Container } from 'inversify';
import type { 
	IEnv, 
	IComputed, 
	WatchCallback, 
	WatchStopHandle,
	IClientRouting,
	IClientStorage,
	ICurrentUser,
	ServiceIdentifier,
	GetOptions,
	LocalizedString
} from '@lingvo-news/contracts';
import { stringifyServiceIdentifier } from '@lingvo-news/contracts';
import { IPopupService } from '@lingvo-news/contracts';
import i18n from '../i18n';
import { formatUserLocalDate } from '../utils/format-user-local-date';

/**
 * Singleton instance of VueEnv
 */
let vueEnvInstance: VueEnv | null = null;

/**
 * Vue implementation of IEnv
 * Wraps Vue's reactive utilities and provides services for framework-agnostic ViewModels
 */
export class VueEnv implements IEnv {
	private container: Container | null = null;

	constructor(
		public readonly currentUser: ICurrentUser,
		public readonly routing: IClientRouting,
		public readonly clientStorage: IClientStorage
	) {}

	get popupService(): IPopupService {
		return this.get(IPopupService);
	}

	setContainer(container: Container): void {
		this.container = container;
	}

	reactive<T extends object>(obj: T): T {
		return reactive(obj) as T;
	}

	computed<T>(getter: () => T): IComputed<T> {
		const computedRef = computed(getter);
		return {
			get value(): T {
				return computedRef.value;
			}
		};
	}

	watch<T>(source: () => T, callback: WatchCallback<T>): WatchStopHandle {
		return watch(source, callback);
	}

	watchMultiple<T extends readonly unknown[]>(
		sources: readonly [...{ [K in keyof T]: () => T[K] }],
		callback: (newValues: T, oldValues: T) => void
	): WatchStopHandle {
		return watch(
			sources as unknown as Array<() => unknown>,
			callback as unknown as (newValue: unknown, oldValue: unknown) => void
		);
	}

	get<T>(serviceIdentifier: ServiceIdentifier<T>, _options?: GetOptions): T {
		if (!this.container) {
			throw new Error('Service container is not initialized. Call registerAllServices() (or equivalent) first.');
		}

		try {
			return this.container.get(serviceIdentifier as never) as T;
		} catch (error) {
			const id = stringifyServiceIdentifier(serviceIdentifier);
			const message = error instanceof Error ? error.message : String(error);
			throw new Error(`Service not found: ${id}. ${message}`);
		}
	}

	formatDate(date: string | Date | null | undefined): string {
		if (!date) {
			return 'Not scheduled';
		}
		return formatUserLocalDate(date, 'Not scheduled');
	}

	showAlert(message: string): void {
		alert(message);
	}

	async showConfirm(message: string): Promise<boolean> {
		return confirm(message);
	}

	nextTick(callback: () => void): void {
		import('vue').then(({ nextTick }) => nextTick(callback));
	}

	log(message: string, data?: unknown): void {
		if (data !== undefined) {
			console.log(message, data);
		} else {
			console.log(message);
		}
	}

	localizedStringToText(value: LocalizedString | null | undefined): string {
		if (!value) return '';
		const fixedValue = (() => {
			const v: unknown = value;
			if (!v || typeof v !== 'object') return undefined;
			if (!('fixedValue' in v)) return undefined;
			const fv = (v as { readonly fixedValue?: unknown }).fixedValue;
			return typeof fv === 'string' ? fv : undefined;
		})();
		const stringId = value.stringId;
		if (!stringId) return fixedValue ?? '';

		const translated = i18n.global.t(stringId, value.formatParams);
		const translatedText = typeof translated === 'string' ? translated : String(translated);

		if (fixedValue) {
			if (value.stringId === 'common.rawText') return fixedValue;
			if (translatedText === value.stringId) return fixedValue;
		}

		return translatedText;
	}
}

/**
 * Initialize VueEnv with routing instance
 */
export function initializeVueEnv(
	currentUser: ICurrentUser,
	routing: IClientRouting,
	clientStorage: IClientStorage
): void {
	vueEnvInstance = new VueEnv(currentUser, routing, clientStorage);
}

export function setVueEnvContainer(container: Container): void {
	if (!vueEnvInstance) {
		throw new Error('VueEnv not initialized. Call initializeVueEnv() first.');
	}
	vueEnvInstance.setContainer(container);
}

/**
 * Get the singleton VueEnv instance
 */
export function getVueEnv(): IEnv {
	if (!vueEnvInstance) {
		throw new Error('VueEnv not initialized. Call initializeVueEnv() first. Make sure main.ts is executed.');
	}
	return vueEnvInstance;
}

/**
 * Check if VueEnv is initialized
 */
export function isVueEnvInitialized(): boolean {
	return vueEnvInstance !== null;
}
